import React from 'react';
import { GitBranch, FileText } from 'lucide-react';

const VERDICT_COLORS = {
  go: '#10B981',
  no_go: '#A61E17',
  conditional_go: '#F2A900',
};

/**
 * ProjectVersionsView — displays all versions sharing a playbook_root_fray_id.
 *
 * Per #1084: when a user iterates a fray (fray_new_version / fork / rerun),
 * the parent is promoted to a project root and all descendants share the
 * same root. This component renders the chronological list of versions.
 *
 * Receives `lineage` from /api/frays/{id}/lineage which includes
 * project_versions[] sorted by lineage_version + created_at.
 *
 * Renders nothing if the fray is solo (no project root yet).
 */
export default function ProjectVersionsView({ lineage, onSelectFray }) {
  if (!lineage) return null;
  const versions = lineage.project_versions || [];
  if (versions.length <= 1) return null;  // Don't render for solo frays

  return (
    <div
      className="mt-6 p-4 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg"
      data-testid="project-versions-section"
    >
      <h3 className="text-sm font-bold font-['Barlow_Condensed'] text-[#F5F1E8] uppercase tracking-wider mb-3 flex items-center gap-2">
        <GitBranch className="w-4 h-4 text-[#F2A900]" />
        Project Versions
        <span className="ml-2 px-2 py-0.5 bg-[#F2A900]/20 text-[#F2A900] text-[10px] font-mono rounded">
          {versions.length}
        </span>
      </h3>
      <p className="text-[10px] font-mono text-[#A0A0A0] mb-3">
        All versions sharing project root{' '}
        <span className="text-[#00D4FF]">
          {lineage.project_root_fray_id?.slice(0, 8) || '—'}
        </span>
      </p>

      <ol className="space-y-1">
        {versions.map((v, i) => {
          const isCurrent = v.is_current;
          const number = v.lineage_version || i + 1;
          return (
            <li
              key={v.id}
              data-testid={`project-version-${i}`}
              className={
                'flex items-center gap-3 px-3 py-2 transition-colors text-xs font-mono ' +
                (isCurrent
                  ? 'bg-[#FF4D00]/10 border-l-2 border-[#FF4D00]'
                  : 'hover:bg-[#2A2A2A]/30 cursor-pointer')
              }
              onClick={
                !isCurrent && onSelectFray
                  ? () => onSelectFray(v.id)
                  : undefined
              }
            >
              <span className="text-[#A0A0A0] w-8">v{number}</span>
              <span
                className={
                  'capitalize ' +
                  (isCurrent ? 'text-[#FF4D00]' : 'text-[#A855F7]')
                }
              >
                {v.lineage_type || 'original'}
              </span>
              <FileText className="w-3 h-3 text-[#A0A0A0]" />
              <span className="text-[#F5F5F5] truncate flex-1">
                {v.template_name || 'Untitled'}
              </span>
              <span
                className={
                  'px-2 py-0.5 rounded text-[10px] ' +
                  (v.status === 'completed'
                    ? 'bg-[#10B981]/20 text-[#10B981]'
                    : v.status === 'imported'
                    ? 'bg-[#A855F7]/20 text-[#A855F7]'
                    : v.status === 'failed'
                    ? 'bg-[#A61E17]/20 text-[#A61E17]'
                    : 'bg-[#2A2A2A] text-[#A0A0A0]')
                }
              >
                {v.status}
              </span>
              {v.verdict && (
                <span
                  className="font-mono"
                  style={{ color: VERDICT_COLORS[v.verdict] || '#A0A0A0' }}
                >
                  {v.verdict}
                </span>
              )}
              <span className="text-[#A0A0A0] text-[10px]">
                {v.created_at
                  ? new Date(v.created_at).toLocaleDateString()
                  : '—'}
              </span>
              {isCurrent && (
                <span className="text-[10px] text-[#FF4D00] uppercase">
                  current
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
